/**
 * 字体警告抑制器
 * 用于抑制浏览器在字体加载过程中产生的各种警告和错误信息
 */

export class FontWarningSuppressor {
  private static instance: FontWarningSuppressor | null = null;
  private originalConsoleError: typeof console.error;
  private originalConsoleWarn: typeof console.warn;
  private isActive = false;

  // 需要抑制的字体相关警告和错误关键词
  private readonly suppressKeywords = [
    "Failed to decode downloaded font",
    "OTS parsing error",
    "Intervention",
    "Slow network is detected",
    "Fallback font will be used",
    "font loading",
    "font-face",
    "woff2",
    "font decode",
    "font parsing",
  ];

  private constructor() {
    this.originalConsoleError = console.error.bind(console);
    this.originalConsoleWarn = console.warn.bind(console);
  }

  public static getInstance(): FontWarningSuppressor {
    if (!FontWarningSuppressor.instance) {
      FontWarningSuppressor.instance = new FontWarningSuppressor();
    }
    return FontWarningSuppressor.instance;
  }

  /**
   * 启用字体警告抑制
   */
  public enable(): void {
    if (this.isActive) {
      return;
    }

    this.isActive = true;

    // 重写 console.error
    console.error = (...args: any[]) => {
      const message = this.argsToString(args);
      if (!this.shouldSuppress(message)) {
        this.originalConsoleError(...args);
      }
    };

    // 重写 console.warn
    console.warn = (...args: any[]) => {
      const message = this.argsToString(args);
      if (!this.shouldSuppress(message)) {
        this.originalConsoleWarn(...args);
      }
    };
  }

  /**
   * 禁用字体警告抑制，恢复原始控制台方法
   */
  public disable(): void {
    if (!this.isActive) {
      return;
    }

    this.isActive = false;
    console.error = this.originalConsoleError;
    console.warn = this.originalConsoleWarn;
  }

  /**
   * 临时抑制警告，执行回调后恢复
   */
  public async withSuppression<T>(callback: () => Promise<T> | T): Promise<T> {
    const wasActive = this.isActive;

    if (!wasActive) {
      this.enable();
    }

    try {
      return await callback();
    } finally {
      if (!wasActive) {
        this.disable();
      }
    }
  }

  /**
   * 将参数转换为字符串
   */
  private argsToString(args: any[]): string {
    return args
      .map((arg) => {
        if (typeof arg === "string") {
          return arg;
        }
        if (typeof arg === "object") {
          try {
            return JSON.stringify(arg);
          } catch {
            return String(arg);
          }
        }
        return String(arg);
      })
      .join(" ");
  }

  /**
   * 检查消息是否应该被抑制
   */
  private shouldSuppress(message: string): boolean {
    const lowerMessage = message.toLowerCase();
    return this.suppressKeywords.some((keyword) =>
      lowerMessage.includes(keyword.toLowerCase()),
    );
  }

  /**
   * 添加自定义抑制关键词
   */
  public addSuppressKeyword(keyword: string): void {
    if (!this.suppressKeywords.includes(keyword)) {
      this.suppressKeywords.push(keyword);
    }
  }

  /**
   * 移除抑制关键词
   */
  public removeSuppressKeyword(keyword: string): void {
    const index = this.suppressKeywords.indexOf(keyword);
    if (index > -1) {
      this.suppressKeywords.splice(index, 1);
    }
  }

  /**
   * 获取当前抑制状态
   */
  public isEnabled(): boolean {
    return this.isActive;
  }
}

// 导出单例实例
export const fontWarningSuppressor = FontWarningSuppressor.getInstance();

// 自动启用字体警告抑制（可选）
if (typeof window !== "undefined") {
  // 在浏览器环境中自动启用
  fontWarningSuppressor.enable();

  // 页面卸载时清理
  window.addEventListener("beforeunload", () => {
    fontWarningSuppressor.disable();
  });
}
